/**
 * Which of your rooms the portal opens on.
 *
 * **localStorage, deliberately unlike the acting role**, which `@brush/auth`'s `actingRole.tsx`
 * keeps in sessionStorage. The two look alike and are not the same kind of answer. A role is
 * "who am I being right now" — a control the server enforces, and a reload mid-task must not
 * silently change what a person can do, so it is scoped to the browsing session. Which of your
 * own cards you are looking at is a *preference*: nothing about it is enforced anywhere, every
 * answer is equally yours, and somebody who holds cards at two rooms should not have to say which
 * one they meant every time they open the app.
 *
 * Same store and same reasoning as room-web's default room (`apps/room-web/src/preferences.ts`),
 * including the trade-off: it does not follow a player between machines. The API has no
 * user-preferences surface, and adding one for this would mean an entity, a migration and
 * endpoints for something that decides which of your own rooms is on screen.
 *
 * Not keyed by anything — this preference *is* the choice of room.
 */

const PORTAL_ROOM_KEY = 'brush.portal.room';

/** The room the portal should open on, or null if the player has never chosen one. */
export function loadPortalRoomId(): string | null {
  try {
    const raw = window.localStorage.getItem(PORTAL_ROOM_KEY);
    return raw !== null && raw.length > 0 ? raw : null;
  } catch {
    // Private browsing or a disabled store. Falling back to asking which room is correct; a
    // preference is never worth failing a screen over.
    return null;
  }
}

export function savePortalRoomId(id: string): void {
  try {
    window.localStorage.setItem(PORTAL_ROOM_KEY, id);
  } catch {
    /* see loadPortalRoomId */
  }
}

export function clearPortalRoomId(): void {
  try {
    window.localStorage.removeItem(PORTAL_ROOM_KEY);
  } catch {
    /* see loadPortalRoomId */
  }
}
